/**
 * Emergency Help v2 (M35-T06, ADR-0064): the read API from T03 and its
 * anonymous usage counts.
 *
 * Nothing here sends anything about the patient. The hospital reads take no
 * parameters, so the patient's location cannot reach the server; distance is
 * worked out in the browser (`lib/distance.ts`). The events carry a kind and,
 * for the hospital kinds, a hospital slug, nothing else.
 */
import { apiFetch } from '../lib/api'

/**
 * One partner hospital as a patient may see it. The gated keys are ABSENT
 * (not null) until AMTC verifies them (T03), so every one is optional here and
 * a screen shows a field only when it is present.
 */
export type EmergencyHelpHospital = {
  slug: string
  name: string
  city: string
  /** Hospital-reported, not AMTC-checked. Null when the hospital has no bed row. */
  emergency_status: string | null
  emergency_info_verified: boolean

  // Present only when emergency_info_verified is true.
  address?: string | null
  emergency_phone?: string | null
  emergency_hours_text?: string | null
  /** Null means unknown: it reads as nothing, never as "no". */
  has_emergency?: boolean | null
  emergency_24h?: boolean | null
  last_checked_at?: string | null

  // Present only when THIS hospital's location is verified.
  location_verified?: boolean
  lat?: number
  lng?: number
}

export type EmergencyHelpHospitals = {
  /** True only when every listed hospital's location is verified. */
  distance_available: boolean
  data: EmergencyHelpHospital[]
}

export const emergencyHelpHospitalsKey = [
  'emergency-help',
  'hospitals',
] as const
export const emergencyHelpHospitalKey = (slug: string) =>
  ['emergency-help', 'hospital', slug] as const

export async function fetchEmergencyHelpHospitals(
  signal?: AbortSignal,
): Promise<EmergencyHelpHospitals> {
  return apiFetch<EmergencyHelpHospitals>(
    '/api/v1/emergency-help/hospitals',
    signal ? { signal } : {},
  )
}

/**
 * One hospital. The server returns it BARE, not wrapped in `{ data }`
 * (`response_model=EmergencyHelpHospital` in `backend/app/api/v1/
 * emergency_help.py`; its test reads `json()["slug"]`), unlike the list. The
 * first build unwrapped `.data` and every hospital screen failed to load,
 * with tests green because their stub invented the same wrapper.
 */
export async function fetchEmergencyHelpHospital(
  slug: string,
  signal?: AbortSignal,
): Promise<EmergencyHelpHospital> {
  return apiFetch<EmergencyHelpHospital>(
    `/api/v1/emergency-help/hospitals/${encodeURIComponent(slug)}`,
    signal ? { signal } : {},
  )
}

export type EmergencyHelpEvent =
  | { kind: 'opened' | 'called_112' }
  | {
      kind: 'viewed_hospital' | 'called_hospital' | 'got_directions'
      hospital_slug: string
    }

/**
 * Fire and forget. The patient's screen never waits on this, never shows its
 * result, and never sees it fail: a rejected or thrown send is swallowed here.
 */
export function sendEmergencyHelpEvent(event: EmergencyHelpEvent): void {
  try {
    void apiFetch('/api/v1/emergency-help/events', {
      method: 'POST',
      body: event,
      // A call or directions tap hands the phone to another app on the same
      // click; keepalive lets the count survive the page being backgrounded.
      keepalive: true,
    }).catch(() => undefined)
  } catch {
    // Nothing: bookkeeping must never reach the patient.
  }
}

// ------------------------------------------------------------------ coordination (M36)

export const coordinationAvailabilityKey = [
  'emergency-help',
  'coordination-availability',
] as const

/** Is a coordinator on duty right now? `available` and nothing else (M36-T04).
 * Anything but a clear yes reads as no. */
export async function fetchCoordinationAvailability(
  signal?: AbortSignal,
): Promise<boolean> {
  const body = await apiFetch<{ available?: unknown }>(
    '/api/v1/emergency-help/coordination-availability',
    signal ? { signal } : {},
  )
  return body.available === true
}

/** Screen 4's request (M36-T05). There is no transport field, and none can be
 * added: the server refuses unknown keys. */
export type CoordinationRequestBody = {
  callback_phone: string
  location_text?: string
  lat?: number
  lng?: number
  description?: string
  hospital_slug?: string
  consent: true
  feedback_consent?: true
}

export async function sendCoordinationRequest(
  body: CoordinationRequestBody,
): Promise<string> {
  const res = await apiFetch<{ reference: string }>(
    '/api/v1/emergency-help/coordination-requests',
    { method: 'POST', body },
  )
  return res.reference
}
